import type { InvoiceLineItemSearchRow } from '@techgitter/contracts';

export type LineItemSortKey = 'invoice' | 'date' | 'customer' | 'code' | 'total';

const collator = new Intl.Collator('en-AU', { sensitivity: 'base', numeric: true });

/**
 * Client-side sort of a result set (≤ 5000 rows). Ties always fall back to
 * newest invoice, then position on the invoice, so lines of one invoice stay
 * together and in their printed order.
 */
export function sortRows(
  rows: InvoiceLineItemSearchRow[],
  key: LineItemSortKey,
  dir: 'asc' | 'desc',
): InvoiceLineItemSearchRow[] {
  const sign = dir === 'asc' ? 1 : -1;
  const primary = (a: InvoiceLineItemSearchRow, b: InvoiceLineItemSearchRow): number => {
    switch (key) {
      case 'invoice':
        return (Number(a.invoiceNumber) || 0) - (Number(b.invoiceNumber) || 0);
      case 'date':
        return (a.invoiceDate ?? '').localeCompare(b.invoiceDate ?? '');
      case 'customer':
        return collator.compare(a.customerName, b.customerName);
      case 'code':
        return collator.compare(a.productCode, b.productCode);
      case 'total':
        return a.lineTotal - b.lineTotal;
      default:
        return 0;
    }
  };
  return [...rows].sort(
    (a, b) =>
      sign * primary(a, b) ||
      (b.invoiceDate ?? '').localeCompare(a.invoiceDate ?? '') ||
      (Number(b.invoiceNumber) || 0) - (Number(a.invoiceNumber) || 0) ||
      a.position - b.position,
  );
}
